// Radha — payment plans: split an invoice's outstanding balance into a manual
// installment schedule (Zoho Billing "payment plan" style). Each installment is
// paid independently through the normal Razorpay flow; the earliest pending one
// settles first when a payment captures (migration 047 trigger).
import React, { useState } from "react";

import { useInvoiceInstallments, useCreateInstallmentPlan, useDeleteInstallment, payErrorMessage } from "../hooks/usePayments";
import { PayButton } from "./PayButton";
import { fmtMoney as inr } from "../lib/money";

const todayPlus = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

export function InstallmentPlan({ invoiceId, outstanding, tenantName, canWrite }: {
  invoiceId: string; outstanding: number; tenantName: string; canWrite: boolean;
}) {
  const { data: installments, isLoading } = useInvoiceInstallments(invoiceId);
  const create = useCreateInstallmentPlan();
  const del = useDeleteInstallment();
  const [count, setCount] = useState(3);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  if (isLoading) return null;
  const rows = installments ?? [];

  if (rows.length === 0) {
    if (!canWrite) return null;
    const per = Math.floor((outstanding / count) * 100) / 100;
    const makePlan = () => {
      setNote(null);
      const parts = Array.from({ length: count }, (_, i) => ({
        amount: i === count - 1 ? outstanding - per * (count - 1) : per, // last installment absorbs rounding
        due_date: todayPlus(30 * (i + 1)),
      }));
      create.mutate({ invoiceId, installments: parts }, {
        onSuccess: () => setNote({ ok: true, text: `Split into ${count} installments` }),
        onError: (e) => setNote({ ok: false, text: payErrorMessage(e) }),
      });
    };
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: "var(--fg-muted)" }}>
        <span>Split into</span>
        <select value={count} onChange={(e) => setCount(Number(e.target.value))}
          style={{ padding: "1px 4px", fontSize: 11.5, border: "1px solid var(--border)", borderRadius: 5, background: "var(--surface)", color: "var(--fg)" }}>
          {[2, 3, 4, 6, 12].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <span>×{inr(per)}/mo</span>
        <button onClick={makePlan} disabled={create.isPending}
          style={{ padding: "1px 8px", fontSize: 11.5, border: "1px solid var(--border)", borderRadius: 5, background: "none", color: "var(--fg-muted)", cursor: "pointer" }}>
          {create.isPending ? "…" : "Plan"}
        </button>
        {note && <span style={{ color: note.ok ? "var(--status-good)" : "var(--status-critical)" }}>{note.text}</span>}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11.5 }}>
      {rows.map((inst) => (
        <div key={inst.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ color: "var(--fg-muted)", minWidth: 74 }}>{inst.due_date} · {inr(Number(inst.amount))}</span>
          {inst.status === "paid" ? (
            <span style={{ color: "var(--status-good)" }}>paid</span>
          ) : inst.status === "pending" ? (
            <>
              <PayButton invoiceId={invoiceId} installmentId={inst.id} amount={Number(inst.amount)} outstanding={Number(inst.amount)} tenantName={tenantName} label="Pay" />
              {canWrite && (
                <button onClick={() => del.mutate({ id: inst.id, invoiceId })} disabled={del.isPending}
                  title="Remove this installment"
                  style={{ padding: "0 5px", fontSize: 11, border: "1px solid var(--border)", borderRadius: 4, background: "none", color: "var(--fg-faint)", cursor: "pointer" }}>
                  ×
                </button>
              )}
            </>
          ) : (
            <span style={{ textTransform: "capitalize", color: "var(--fg-muted)" }}>{inst.status}</span>
          )}
        </div>
      ))}
    </div>
  );
}
